import api from '../../utils/api';
import { DoctorClinicResponse, SpecialityFeeTableRowDto, SpecialityWiseFeePair } from '../../models/MasterAdmin/doctorClinic';
import { AppointmentResponse } from '../../models/MasterAdmin/doctorClinicAppointment';

export interface UpsertSpecialityFeePayload {
  specialityId?: string;
  feesByConsultationType: {
    online?: SpecialityWiseFeePair;
    offline?: SpecialityWiseFeePair;
  };
}

export interface SpecialityFeeMutationResponse {
  success: boolean;
  message: string;
  data?: SpecialityFeeTableRowDto;
}

export const fetchDoctorClinicProfile = async (vendorId: string): Promise<DoctorClinicResponse> => {
  const response = await api.get(`/master-admin/doctor-clinic/${vendorId}`);
  return response.data;
};

export const updateDoctorClinicProfilePicture = async (
  vendorId: string,
  file: File
): Promise<{ success: boolean; clinic?: any; message?: string }> => {
  const formData = new FormData();
  formData.append('profilePicture', file);
  const response = await api.put(`/clinic/profile/${vendorId}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const createDoctorClinicSpecialityFee = async (
  vendorId: string,
  payload: UpsertSpecialityFeePayload
): Promise<SpecialityFeeMutationResponse> => {
  const response = await api.post(
    `/master-admin/doctor-clinic/${vendorId}/speciality-fees`,
    payload
  );
  return response.data;
};

export const updateDoctorClinicSpecialityFee = async (
  vendorId: string,
  feeId: string,
  payload: UpsertSpecialityFeePayload
): Promise<SpecialityFeeMutationResponse> => {
  const response = await api.put(
    `/master-admin/doctor-clinic/${vendorId}/speciality-fees/${feeId}`,
    payload
  );
  return response.data;
};

export const deleteDoctorClinicSpecialityFee = async (
  vendorId: string,
  feeId: string
): Promise<SpecialityFeeMutationResponse> => {
  const response = await api.delete(
    `/master-admin/doctor-clinic/${vendorId}/speciality-fees/${feeId}`
  );
  return response.data;
};

export const toggleVendorStatus = async (vendorId: string) => {
  const response = await api.put(`/master-admin/vendor/${vendorId}/toggle-status`);
  return response.data;
};

export const fetchOnlineAppointments = async (vendorId: string): Promise<AppointmentResponse> => {
  const response = await api.get(`/master-admin/appointments/online/${vendorId}`);
  return response.data;
};

export const fetchOfflineAppointments = async (vendorId: string): Promise<AppointmentResponse> => {
  const response = await api.get(`/master-admin/appointments/offline/${vendorId}`);
  return response.data;
};

export interface DeleteDoctorAccountPayload {
  confirmDoctorName: string;
  reason?: string;
}

export interface DeleteDoctorAccountResponse {
  success: boolean;
  message: string;
  data?: {
    vendorId: string;
    doctorName: string;
    deletedAt: string;
    logoutForced?: boolean;
    sessionsRevoked?: boolean;
  };
}

export const deleteDoctorAccount = async (
  vendorId: string,
  payload: DeleteDoctorAccountPayload
): Promise<DeleteDoctorAccountResponse> => {
  const response = await api.delete(`/master-admin/doctor-clinic/${vendorId}/delete-account`, {
    data: payload,
  });
  return response.data;
}; 

export const fetchClinicAppointmentInvoiceBlob = async (
  clinicAppointmentId: string,
  userType: 'user' | 'vendor' | 'masteradmin' = 'user'
): Promise<Blob> => {
  const encodedId = encodeURIComponent(clinicAppointmentId);
  const response = await api.get(`/clinic-invoice/invoice/${encodedId}`, {
    params: { userType },
    responseType: 'blob',
  });
  return response.data as Blob;
};

export const fetchDoctorDigitalCardUrl = async (
  vendorId: string
): Promise<{ success: boolean; cardUrl: string; expiresAt: string }> => {
  const response = await api.get(`/clinic/profile/${vendorId}/digital-card/url`);
  return response.data;
};